/** @typedef {import('../../types/user.types.js').AuthApiResponse} AuthApiResponse */
import {ERROR_CODE_HEADER} from '../../../core/vocabulary.js';

export class ApiError extends Error {
  /** @param {number} status
   *  @param {string} message
   *  @param {string|null} [code] the kind of failure the backend names in its `X-Error-Code` header */
  constructor(status, message, code = null) {
    super(message);
    this.status = status;
    this.code = code;
  }

  /**
   * The error for a failed response: its status, its body as the message and the error code
   * from its `X-Error-Code` header, when the backend sends one.
   * @param {Response} response
   * @param {string} [fallbackMessage] used when the body is empty
   * @param {number} [fallbackStatus] used when the response carries no status
   * @returns {Promise<ApiError>}
   */
  static async fromResponse(response, fallbackMessage = '', fallbackStatus = 500) {
    const message = await response.text();
    const code = response.headers?.get?.(ERROR_CODE_HEADER) || null;
    return new ApiError(response.status || fallbackStatus, message || fallbackMessage, code);
  }
}

export class AuthApiAdapter {
  #baseUrl;

  /** @param {string} baseUrl - e.g. '/api/auth' */
  constructor(baseUrl) {
    this.#baseUrl = baseUrl;
  }

  /** @returns {Promise<AuthApiResponse>} */
  async login(request) {
    return this.#request('/login', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /** @returns {Promise<AuthApiResponse>} */
  async register(request) {
    return this.#request('/register', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  /**
   * @param {string} token
   * @returns {Promise<AuthApiResponse>}
   */
  async refresh(token) {
    return this.#request('/refresh', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  async #request(path, options = {}) {
    const response = await fetch(`${this.#baseUrl}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw await ApiError.fromResponse(response);
    }

    return response.json();
  }
}
